export const formatPrice = (
  value: number | string | null | undefined,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '-';

  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return '-';

  const fixed = num.toFixed(decimals);
  const [intPart, decimalPart] = fixed.split('.');

  const intWithSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  const formatted =
    decimalPart && decimals > 0
      ? `${intWithSpaces},${decimalPart}`
      : intWithSpaces;

  return `${formatted} €`;
};

export const getStatusStyle = (status: string) => {
  if (!status) {
    return { color: '#6B7280', backgroundColor: 'rgba(107, 114, 128, 0.1)' }; // neutre
  }

  const normalized = status.trim().toLowerCase();

  switch (normalized) {
    // ✅ Accepté / Validé
    case 'valider':
    case 'valide':
    case 'validé':
    case 'accepté':
    case 'accepte':
    case 'acceptée':
    case 'accepter':
      return { color: '#00A708', backgroundColor: 'rgba(0, 167, 8, 0.1)' };

    // ✅ Refusé
    case 'refuser':
    case 'refus':
    case 'refusé':
    case 'refusée':
    case 'refuse':
      return { color: '#D90E0E', backgroundColor: 'rgba(217, 14, 14, 0.1)' };

    // ✅ Brouillon
    case 'brouillon':
    case 'brouillon prestataire':
    case 'brouillon acheteur':
    case 'en attente':
    case 'en attente de votre acceptation':
    case 'en attente de décision de l\'acheteur':
    case "en attente de l'offre":
      return { color: '#6B7280', backgroundColor: 'rgba(107, 114, 128, 0.1)' };

    // ✅ À valider
    case 'a signer':
    case 'à signer':
    case 'à accepter':
    case 'à valider':
      return { color: '#F4751B', backgroundColor: 'rgba(244, 117, 27, 0.1)' };

    // ✅ À chiffrer
    case 'a chiffrer':
    case 'à chiffrer':
    case 'chiffrer':
      return { color: '#F4751B', backgroundColor: 'rgba(244, 117, 27, 0.1)' };


    // ✅ En analyse
    case 'analyse':
    case 'à analyser':
    case 'analyser':
      return { color: '#F4751B', backgroundColor: 'rgba(244, 117, 27, 0.1)' };


    // ✅ Expiré
    case 'expirer':
    case 'expiré':
    case 'expirée':
    case 'expire':
      return { color: '#D90E0E', backgroundColor: 'rgba(217, 14, 14, 0.1)' };

    // ✅ Action requise
    case 'action requise':
      return { color: '#F4751B', backgroundColor: 'rgba(244, 117, 27, 0.1)' };

    // ✅ Candidater
    case 'candidater':
    case 'à candidater':
      return { color: '#F4751B', backgroundColor: 'rgba(244, 117, 27, 0.1)' };

    default:
      return { color: '#6B7280', backgroundColor: 'rgba(107, 114, 128, 0.1)' };
  }
};

export const getStatusColor = (status: string): string => {
  return getStatusStyle(status).color;
};


export const formatDate = (
  dateInput: string | Date,
  withTime: boolean = true
): string => {
  if (!dateInput) return 'Date inconnue';

  let date: Date;

  if (typeof dateInput === 'string') {
    // On nettoie les formats ISO ou SQL
    const cleanDate = dateInput.replace(' ', 'T').replace('Z', '');
    date = new Date(cleanDate);
  } else {
    date = dateInput;
  }

  // Vérifie si la date est valide
  if (isNaN(date.getTime())) {
    return 'Date invalide';
  }

  // Formate manuellement sans dépendre de la locale
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  if (!withTime) {
    return `${day}/${month}/${year}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} à ${hours}:${minutes}:${seconds}`;
};

export const formatStatusName = (status: string): string => {
  console.log('status sent to formatStatusName:', status)
  if (!status) return 'Brouillon';

  const normalized = status.trim().toLowerCase();

  switch (normalized) {
    // ✅ Accepté
    case 'valide':
    case 'valider':
    case 'accepte':
    case 'accepté':
    case 'acceptée':
      return 'Accepté';

    case 'à accepter':
    case 'a accepter':
      return 'À accepter';

    // ✅ Refusé
    case 'refus':
    case 'refuser':
    case 'refuse':
    case 'refusé':
    case 'refusée':
      return 'Refusé';

    // ✅ Brouillon
    case 'brouillon':
    case 'brouillon prestataire':
    case 'brouillon acheteur':
      return 'Brouillon';

    // ✅ En attente
    case 'en attente':
    case "en attente de l'offre":
    case 'en attente de votre acceptation':
      return 'En attente';

    // ✅ À chiffrer
    case 'chiffrer':
    case 'a chiffrer':
    case 'à chiffrer':
      return 'À chiffrer';

    // ✅ En analyse
    case 'analyse':
    case 'analyser':
    case 'en analyse':
      return 'En analyse';

    // ✅ Candidater
    case 'candidater':
      return 'Candidater';

    // ✅ Signé
    case 'signer':
    case 'signé':
    case 'signée':
      return 'Signé';

    // ✅ Expiré
    case 'expirer':
    case 'expiré':
    case 'expirée':
      return 'Expiré';

    // ✅ Action requise
    case 'action requise':
      return 'Action requise';

    default:
      // Par défaut on garde la première lettre en majuscule
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }
};

export const formatApiDate = (apiDate: string): string => {
  if (!apiDate) return '';
  try {
    const date = new Date(apiDate);
    return date.toLocaleDateString('fr-FR');
  } catch (e) {
    return '';
  }
};

export const calculateFinancialScore = (entities: any[]): { [key: string]: number } => {
  if (!entities || entities.length === 0) return {};

  const montants = entities.map((entity, idx) => {
    let totalMontant = 0;

    if (entity.sections) {
      entity.sections.forEach(section => {
        if (section.articles) {
          section.articles.forEach(article => {
            if (article.prix) {
              totalMontant += parseFloat(article.prix);
            }
          });
        }
      });
    }

    return {
      id: idx.toString(),
      montant: totalMontant || 10000 + (idx * 1000)
    };
  });

  const minMontant = Math.min(...montants.map(m => m.montant));
  const maxMontant = Math.max(...montants.map(m => m.montant));
  const scores: { [key: string]: number } = {};

  if (montants.length === 1) {
    scores[montants[0].id] = 10;
  } else if (montants.length === 2) {
    montants.forEach(m => {
      scores[m.id] = m.montant === minMontant ? 10 : 0;
    });
  } else {
    montants.forEach(m => {
      if (m.montant === minMontant) {
        scores[m.id] = 10;
      } else if (m.montant === maxMontant) {
        scores[m.id] = 0;
      } else {
        const plageTotal = maxMontant - minMontant;
        const position = maxMontant - m.montant;
        scores[m.id] = Math.round((position / plageTotal) * 10);
      }
    });
  }

  return scores;
};

export const getNoteGlobale = (techNotes: any, entityId: string): string => {
  const criteres = ['organisation', 'moyens', 'social'];
  let totalPoints = 0;
  let maxPossiblePoints = 0;

  const pointsMax = {
    'organisation': 5,
    'moyens': 4,
    'social': 1
  };

  criteres.forEach(critere => {
    const note = techNotes[entityId]?.[critere]?.note || 0;
    totalPoints += note;
    maxPossiblePoints += pointsMax[critere];
  });

  return maxPossiblePoints > 0 ? ((totalPoints / maxPossiblePoints) * 10).toFixed(2) : '0.00';
};

export const isAcheteurSignataire = (): boolean => {
  try {
    const userString = localStorage.getItem('user');
    if (!userString) return false;

    let userData;
    try {
      userData = JSON.parse(userString);
    } catch (parseError) {
      if (userString.startsWith('"') && userString.endsWith('"')) {
        try {
          const cleanedString = userString.substring(1, userString.length - 1);
          userData = JSON.parse(cleanedString);
        } catch (secondParseError) {
          return false;
        }
      } else {
        return false;
      }
    }

    const userRole = userData.role;
    return userRole === 'Acheteur signataire admin' || userRole === 'Acheteur signataire';
  } catch (error) {
    console.error('Erreur lors de la vérification du rôle:', error);
    return false;
  }
};